// src/store/modules/personSkills.js
import api from "@/config/api";
import { handleApiError } from "@/utils/ErrorHandler";
import { createPaginationModule, defaultPagination } from "@/store/common/pagination";

const personSkillsPag = createPaginationModule('PERSON_SKILLS');

export default {
  state: {
    skills: [],
    loadingSkills: false,
    saveSkillsResponse: null,
    personSkillDetails: null,
    saveSkillLevelResponse: null,
    personSkillsModal: null,
    pagination: defaultPagination(),
  },

  mutations: {
    ...personSkillsPag.mutations,

    setSkills(state, payload) {
      state.skills = payload;
    },
    setLoadingSkills(state, payload) {
      state.loadingSkills = payload;
    },
    setSaveSkillsResponse(state, payload) {
      state.saveSkillsResponse = payload;
    },
    setPersonSkillDetails(state, payload) {
      state.personSkillDetails = payload;
    },
    setSaveSkillLevelResponse(state, payload) {
      state.saveSkillLevelResponse = payload;
    },
    setPersonSkillsModal(state, payload) {
      state.personSkillsModal = payload;
    },
  },

  actions: {
    // ✅ Paginated + searchable fetch for person skills
    async fetchPersonSkills(
      { commit },
      { personId, locationId, search = "", page = 1, pageSize = 10 }
    ) {
      try {
        const response = await api.get(
          `/manage-user/person-skills?locationid=${locationId}&personid=${personId}&page_size=${pageSize}&page_number=${page}&search=${encodeURIComponent(
            search
          )}`
        );
        console.log("response.data", response.data);

        const { data, pagination } = response.data;

        commit(personSkillsPag.SET_PAGINATION, {
          currentPage: pagination.current_page,
          totalPages: pagination.total_pages,
          totalRecords: pagination.total_records,
          pageSize: pagination.page_size,
          hasPrevious: pagination.has_previous,
          hasNext: pagination.has_next,
        });

        return data;

      } catch (error) {
        handleApiError(error, "Failed to fetch person skills");
        throw error;
      }
    },

    // fetch person skills modal
    async fetchPersonSkillsModal({ commit }, { personId, locationId, peopleGroupId }) {
      commit("setLoadingSkills", true);
      try {
        const response = await api.post("/manageuserajax", {
          cmd: "getpersonskillsmodal",
          personid: personId,
          locationid: locationId,
          people_group_id: peopleGroupId,
        });

        commit("setPersonSkillsModal", response.data);
        return response.data;
      } catch (error) {
        handleApiError(error, "Failed to fetch person skills modal");
        throw error;
      } finally {
        commit("setLoadingSkills", false);
      }
    },

    // save person skills
    async savePersonSkills({ commit }, { personId, skillsArray }) {
      try {
        const response = await api.post("/manageuserajax", {
          cmd: "savepersonskills",
          personid: personId,
          skillsarray: skillsArray,
        });

        commit("setSaveSkillsResponse", response.data);
        return response.data;
      } catch (error) {
        handleApiError(error, "Failed to save person skills");
        throw error;
      }
    },

    // save single skill level
    async saveSkillLevel({ commit }, { skillId, locationId, skillLevel, personId }) {
      try {
        const response = await api.post("/manageskillajax", {
          cmd: "saveskilllevel",
          skillid: skillId,
          locationid: locationId,
          skilllevel: skillLevel,
          personid: personId,
        });

        commit("setSaveSkillLevelResponse", response.data);
        return response.data;
      } catch (error) {
        handleApiError(error, "Failed to save skill level");
        throw error;
      }
    },

    // fetch skill details
    async fetchSkillDetails({ commit }, { skillId, personId }) {
      commit("setLoadingSkills", true);
      try {
        const response = await api.get(`/skills/${skillId}?personid=${personId}`);
        const skillDetails = response.data;
        commit("setPersonSkillDetails", skillDetails);
        return skillDetails;
      } catch (error) {
        handleApiError(error, "Failed to fetch skill details");
        throw error;
      } finally {
        commit("setLoadingSkills", false);
      }
    },
  },

  getters: {
    getPersonSkills: (state) => state.skills,
    isSkillsLoading: (state) => state.loadingSkills,
    getSaveSkillsResponse: (state) => state.saveSkillsResponse,
    getPersonSkillDetails: (state) => state.personSkillDetails,
    getSaveSkillLevelResponse: (state) => state.saveSkillLevelResponse,
    getPersonSkillsModal: (state) => state.personSkillsModal,
    personSkillsPagination: (state) => state.pagination,
  },
};